import React, { useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { debounce } from "lodash";
import dayjs from "dayjs";
import useApi from "../hook/useApi";
import Loader from "./Loader/Loader";
import Paggination from "./Paggination";
import { copyToClipboard, trimAddress } from "../utils/helper";
import { IoCopyOutline } from "react-icons/io5";

const RewardList = () => {
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState({
    pageNo: 1,
    limitVal: 50,
    search: "",
  });

  const { AsyncGetApiCall } = useApi();

  const fetchRewards = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("token");
      const queryParams = {
        pageNo: query?.pageNo,
        limitVal: query?.limitVal,
        search: query?.search,
      };

      const result = await AsyncGetApiCall("/flytoken/list", queryParams, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (result?.errors) toast.error(result.errors[0]);
      else setResponse(result);

      setLoading(false);
    } catch (err) {
      setLoading(false);
      toast.error("Something went wrong");
    }
  };

  useEffect(() => {
    fetchRewards();
  }, [query]);

  const debouncedSearch = useCallback(
    debounce((value) => {
      setQuery((prev) => ({ ...prev, search: value, pageNo: 1 }));
    }, 500),
    []
  );

  const handleSearchChange = (e) => debouncedSearch(e.target.value);

  const rewards = response?.data ?? [];
  const pagination = response?.pagination ?? {};
  const { total = 0, totalPages = 0, currentPage = 1 } = pagination;

  return (
    <>
      {loading && <Loader />}

      <div className="card h-100 p-0 radius-12">
        {/* Header */}
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex flex-wrap gap-3 justify-content-between">
          <div className="d-flex align-items-center gap-3 flex-wrap w-100">
            {/* Limit selector */}
            <span className="text-md fw-medium text-secondary-light">Show</span>
            <select
              className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
              value={query.limitVal}
              onChange={(e) =>
                setQuery((prev) => ({
                  ...prev,
                  limitVal: Number(e.target.value),
                  pageNo: 1,
                }))
              }
            >
              {Array.from({ length: 20 }, (_, i) => (i + 1) * 5).map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>

            {/* Search */}
            {/* <form className="navbar-search">
              <input
                type="text"
                className="bg-base h-40-px w-auto"
                placeholder="Search by Booking ID or User"
                onChange={handleSearchChange}
              />
              <Icon icon="ion:search-outline" className="icon" />
            </form> */}
          </div>
        </div>

        {/* Table */}
        <div className="card-body p-24">
          <div className="table-responsive scroll-sm">
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Booking ID</th>
                  <th>User</th>
                  <th>Email</th>
                  <th>Paid Amount</th>
                  <th>Points</th>
                  <th>Points Value</th>
                  <th>Reward Type</th>
                  <th>Booking Type</th>
                  <th>Date</th>
                </tr>
              </thead>

              <tbody>
                {rewards.length ? (
                  rewards.map((r, i) => {
                    const rowNo = (currentPage - 1) * query.limitVal + i + 1;
                    const user = r.userDetails;

                    return (
                      <tr key={r._id}>
                        <td>{rowNo}</td>

                        <td>
                          {trimAddress(r?.bookingId, 5, 3) || "N/A"}
                          {r?.bookingId && (
                            <button
                              onClick={() => copyToClipboard(r?.bookingId)}
                              className="ms-3 p-0 border-0 bg-transparent"
                              style={{ lineHeight: 1 }}
                            >
                              <IoCopyOutline />
                            </button>
                          )}
                        </td>

                        {/* User Name */}
                        <td>
                          {`${(user?.firstName || '')} ${(user?.lastName || '')}`.trim() || 'N/A'}
                        </td>

                        {/* Email */}
                        <td>{user?.email || "N/A"}</td>

                        {/* Amount */}
                        <td>
                          {!r?.pointsCurrency && !r?.amount ? "N/A" :
                            `${r?.pointsCurrency} ${r?.amount?.toFixed(2)}`
                          }
                        </td>

                        {/* Points */}
                        <td>{r?.points ? r?.points?.toFixed(2) : "N/A"}</td>

                        <td>
                          {!r?.pointsCurrency && !r?.pointsValue ? "N/A" :
                            `${r?.pointsCurrency} ${r?.pointsValue?.toFixed(2)}`
                          }
                        </td>

                        {/* Reward Type */}
                        <td>
                          <span
                            className={`px-12 py-4 radius-4 fw-medium text-sm ${r.rewardType === "EARNED"
                              ? "bg-success-focus text-success-600"
                              : "bg-danger-focus text-danger-600"
                              }`}
                          >
                            {r?.rewardType || "N/A"}
                          </span>
                        </td>

                        <td>{r?.bookingType || "N/A"}</td>
                        <td>{
                          new Date(r.createdAt).toLocaleString("en-GB", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                            hour12: true,
                          })
                        }</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center">
                      No rewards found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <Paggination
            total={total}
            totalPages={totalPages}
            currentPage={currentPage}
            startFrom={(currentPage - 1) * query.limitVal + 1}
            endTo={(currentPage - 1) * query.limitVal + rewards.length}
            setPageNo={(page) =>
              setQuery((prev) => ({ ...prev, pageNo: page }))
            }
          />
        </div>
      </div>
    </>
  );
};

export default RewardList;
